<script lang="ts">
  import { onMount } from "svelte";
  import { resume, session } from "./net/session.svelte.js";
  import { ship } from "./net/ship.svelte.js";
  import { audio } from "./audio/bus.js";
  import JoinTerminal from "./screens/JoinTerminal.svelte";
  import AccessGate from "./screens/AccessGate.svelte";
  import ShipTerminal from "./screens/ShipTerminal.svelte";
  import EndCard from "./screens/EndCard.svelte";
  import ReconnectOverlay from "./components/ReconnectOverlay.svelte";
  import StationView from "./station/StationView.svelte";
  import CrtScreen from "./components/CrtScreen.svelte";
  import { screenSignal } from "./lib/signal.js";
  import { defaultStationView } from "./lib/view.js";
  import ShipMenu from "./components/ShipMenu.svelte";
  import { menu } from "./lib/menu.svelte.js";
  import { gate } from "./net/gate.svelte.js";
  const plain = !defaultStationView();

  onMount(() => {
    void gate.load();
    void resume();
    // Ask for sound immediately: if this origin has earned autoplay the entry theme starts with the
    // page, which is how arriving at the dock should feel. If not, the context sits suspended and
    // any gesture below lets it run. Not `once`: a rejected resume must be retryable.
    audio.unlock();
    const unlock = () => { audio.unlock(); };
    for (const ev of ["pointerdown", "keydown", "touchend", "wheel"] as const) window.addEventListener(ev, unlock);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const el = document.activeElement as HTMLElement | null;
      if (!menu.open && el && /INPUT|TEXTAREA/.test(el.tagName)) { el.blur(); return; } // Escape leaves a field first
      menu.open = !menu.open;
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      for (const ev of ["pointerdown", "keydown", "touchend", "wheel"] as const) window.removeEventListener(ev, unlock);
    };
  });

  const inRoom = $derived((session.status === "connected" || session.status === "reconnecting") && ship.snap !== null);
  const ended = $derived(inRoom && (ship.snap!.phase === "won" || ship.snap!.phase === "lost"));

  /**
   * The entry theme, and only the entry theme. Music belongs to the gangway — the wait outside the
   * hatch, where nothing is at stake yet. Past the hatch the ship is the score: a bed under the
   * intercom would fight the voices, which are the game (pillar 1). Boarding fades it out.
   */
  const MENU_MUSIC = "/audio/reactor-in-the-hull.ogg";
  $effect(() => {
    if (session.status === "boot") return;
    if (inRoom) audio.stopMusic();
    else audio.playMusic(MENU_MUSIC, { level: 0.85 });
  });
</script>

{#if session.status === "boot" || gate.config === null}
  <CrtScreen fill><p class="dim" style="padding:1rem">BOOTING TERMINAL…</p></CrtScreen>
{:else if !inRoom}
  {#if plain}
    <CrtScreen fill station="gangway" signal={screenSignal(ship.snap)}>
      {#if gate.unlocked}<JoinTerminal />{:else}<AccessGate />{/if}
    </CrtScreen>
  {:else}
    <StationView room="gangway" caption={gate.unlocked ? "GANGWAY 2 — CREW ACCESS" : "GANGWAY 2 — DOCKING CONTROL"}>
      {#snippet screen()}{#if gate.unlocked}<JoinTerminal />{:else}<AccessGate />{/if}{/snippet}
    </StationView>
  {/if}
{:else if ended}
  <CrtScreen fill signal={screenSignal(ship.snap)}><EndCard /></CrtScreen>
{:else}
  <ShipTerminal />
{/if}

<button class="menu-link" onclick={() => (menu.open = true)} aria-haspopup="dialog">MENU · ESC</button>
{#if menu.open}<ShipMenu />{/if}

{#if session.status === "reconnecting"}
  <ReconnectOverlay />
{/if}

<style>
  /* Deliberately outside the fiction's furniture: the one control that is the player's, not the crew's. */
  .menu-link {
    position: fixed; top: 0.5rem; right: 0.6rem; z-index: 30;
    min-height: 0; padding: 0.25em 0.6em; font-size: 0.75em;
    border-color: transparent; color: var(--fg-dim); background: rgba(0, 0, 0, 0.35);
  }
  .menu-link:hover, .menu-link:focus-visible { color: var(--fg); border-color: var(--fg-dim); }
</style>
